import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
// NOTE: @capgo/capacitor-updater is imported DYNAMICALLY below, inside
// the isNative branch. Importing it eagerly at the top broke the web
// bundle (white screen) — its module-load code path hit something the
// web shim didn't tolerate.

// Check if running on native platform
export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'

/**
 * Initialize Capacitor plugins and native features
 */
export const initializeCapacitor = async () => {
  if (!isNative) {
    console.log('[Capacitor] Running on web, skipping native initialization');
    return;
  }

  // OTA handshake — fire-and-forget BEFORE any other await below so
  // Capgo's "did the new bundle load cleanly?" timer is cleared as
  // early as possible after the WebView comes up. If this doesn't fire
  // within appReadyTimeout, Capgo auto-rolls back to the previous
  // bundle on next launch — the "sticky rollback loop" that kept the
  // OTA swap from landing. Dynamically imported so the web bundle
  // never touches Capgo's web shim.
  import('@capgo/capacitor-updater')
    .then(({ CapacitorUpdater }) => CapacitorUpdater.notifyAppReady()
      .then(r => console.log('[OTA] notifyAppReady OK:', r))
      .catch(e => console.log('[OTA] notifyAppReady skipped:', e?.message || e)))
    .catch(e => console.log('[OTA] CapacitorUpdater module failed to load:', e?.message || e));

  console.log(`[Capacitor] Initializing on ${platform}`);

  // Tag <html> so CSS can lock the document scroll (kills iOS rubber-band).
  document.documentElement.classList.add('capacitor-native', `capacitor-${platform}`);

  try {
    // Hide splash screen after app loads
    await SplashScreen.hide();

    // Configure status bar
    if (platform === 'ios' || platform === 'android') {
      await StatusBar.setStyle({ style: Style.Dark });
      if (platform === 'android') {
        await StatusBar.setBackgroundColor({ color: '#2563eb' });
      }
    }

    // Setup keyboard listeners
    setupKeyboardListeners();

    // Setup app state listeners
    setupAppListeners();

    console.log('[Capacitor] Native initialization complete');
  } catch (error) {
    console.error('[Capacitor] Initialization error:', error);
  }
};

/**
 * Setup keyboard event listeners for mobile
 */
const setupKeyboardListeners = () => {
  Keyboard.addListener('keyboardWillShow', (info) => {
    document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
    document.body.classList.add('keyboard-open');
  });

  Keyboard.addListener('keyboardWillHide', () => {
    document.body.style.setProperty('--keyboard-height', '0px');
    document.body.classList.remove('keyboard-open');
  });
};

/**
 * Setup app lifecycle listeners
 */
const setupAppListeners = () => {
  App.addListener('appStateChange', ({ isActive }) => {
    console.log('[Capacitor] App state changed, isActive:', isActive);
    // You can refresh data when app becomes active
    if (isActive) {
      window.dispatchEvent(new CustomEvent('app-resumed'));
      // Foreground push self-heal — iOS drops APS subscriptions on its
      // own (OS updates, background termination cascades, low storage)
      // regardless of any auth-side issues. Re-attempting register()
      // on resume gets us a fresh token if the OS regenerated one,
      // and a bounded retry surfaces broken push before the next
      // inbound would have silently rotted. Throttled to at most once
      // per PUSH_REVERIFY_INTERVAL_MS so a rapid switch between apps
      // doesn't hammer APNs / FCM.
      pushForegroundReverify().catch((e) =>
        debugLog('foreground_reverify_error', { error: String(e?.message || e) })
      );
    }
  });

  App.addListener('appUrlOpen', (data) => {
    console.log('[Capacitor] App opened with URL:', data.url);
    // Handle deep links here
  });

  // Handle back button on Android
  App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      App.exitApp();
    }
  });
};

/**
 * Request push notification permissions and register
 */
// Diagnostic: POST device-side push state to the backend so we can read it
// from the server logs (TestFlight builds don't expose console).
const debugLog = (event, data = {}) => {
  try {
    // Inline require to avoid an import cycle — hosts.js is trivial and
    // this diagnostic runs at most once per event.
    const { PUBLIC_HOST } = require('../utils/hosts');
    const apiBase = (typeof window !== 'undefined' && window.location && window.location.origin && !window.location.origin.startsWith('capacitor'))
      ? `${window.location.origin}/api`
      : `${PUBLIC_HOST}/api`;
    fetch(`${apiBase}/push/debug`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, platform, ...data })
    }).catch(() => {});
  } catch (e) {}
};

// Registration retry policy — bounded backoff. A single register() call
// can hang silently when APNs / FCM are having a bad day (stale
// provisioning profile, network partition, OS mid-restart). "Log and
// move on" was masking real failures; retry until we either get a
// token, get a hard error, or exhaust attempts.
const PUSH_REGISTER_TIMEOUT_MS = 10000;
const PUSH_REGISTER_MAX_ATTEMPTS = 3;
const PUSH_REGISTER_BACKOFF_MS = [3000, 8000]; // between attempts 1→2, 2→3
// Foreground re-verify — throttled so a rapid app switch doesn't
// hammer APNs / FCM. 30 min is aggressive enough to catch OS-side
// token regenerations quickly without being noisy.
const PUSH_REVERIFY_INTERVAL_MS = 30 * 60 * 1000;

// Module-level state so initializePushNotifications is idempotent
// (adding listeners twice would double-fire the token callback) and
// the foreground re-verify can share the same handlers.
let pushListenersAttached = false;
let pushOnTokenReceived = null;
let pushLastEventTs = 0;         // 'registration' OR 'registrationError'
let pushLastReverifyTs = 0;
let pushInitialized = false;

const attachPushListenersOnce = async () => {
  if (pushListenersAttached) return;
  pushListenersAttached = true;
  await PushNotifications.addListener('registration', (token) => {
    console.log('[Push] Registration token:', token.value);
    debugLog('registration', { tokenPrefix: (token.value || '').slice(0, 12) });
    pushLastEventTs = Date.now();
    if (pushOnTokenReceived) pushOnTokenReceived(token.value);
  });
  await PushNotifications.addListener('registrationError', (error) => {
    console.error('[Push] Registration error:', error);
    debugLog('registration_error', { error: String(error?.error || error) });
    pushLastEventTs = Date.now();
  });
  await PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[Push] Notification received:', notification);
    window.dispatchEvent(new CustomEvent('push-notification', { detail: notification }));
  });
  await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
    console.log('[Push] Notification action performed:', notification);
    const data = notification.notification.data;
    if (data?.conversationId) {
      window.location.href = `/messages?conversation=${data.conversationId}`;
    }
  });
};

// Attempt PushNotifications.register() once and resolve when the
// registration event fires, reject on registrationError, or reject on
// timeout. Uses pushLastEventTs (module-level, updated by the shared
// listeners) so we don't need per-attempt listener plumbing.
const attemptRegister = async () => {
  const startedAt = Date.now();
  try {
    await PushNotifications.register();
  } catch (e) {
    throw new Error(`register_call_failed: ${e.message || e}`);
  }
  return new Promise((resolve, reject) => {
    const poll = setInterval(() => {
      if (pushLastEventTs > startedAt) {
        clearInterval(poll);
        resolve('registered');
      } else if (Date.now() - startedAt > PUSH_REGISTER_TIMEOUT_MS) {
        clearInterval(poll);
        reject(new Error('register_timeout'));
      }
    }, 300);
  });
};

const registerWithRetry = async () => {
  for (let attempt = 1; attempt <= PUSH_REGISTER_MAX_ATTEMPTS; attempt++) {
    try {
      await attemptRegister();
      debugLog('register_ok', { attempt });
      return true;
    } catch (e) {
      const willRetry = attempt < PUSH_REGISTER_MAX_ATTEMPTS;
      debugLog('register_retry', { attempt, reason: e.message, willRetry });
      if (willRetry) {
        const wait = PUSH_REGISTER_BACKOFF_MS[attempt - 1] || 10000;
        await new Promise((r) => setTimeout(r, wait));
      } else {
        debugLog('register_failed_final', { attempts: attempt });
        return false;
      }
    }
  }
  return false;
};

export const initializePushNotifications = async (onTokenReceived) => {
  if (!isNative) {
    console.log('[Push] Not on native platform, skipping push setup');
    return null;
  }

  debugLog('init_start');

  // Idempotent: subsequent calls just update the token callback and
  // trigger a fresh re-register. Listeners are attached exactly once.
  pushOnTokenReceived = onTokenReceived || pushOnTokenReceived;

  try {
    // Log the OS-cached state separately from the (possibly prompting) request.
    try {
      const checked = await PushNotifications.checkPermissions();
      debugLog('check_perms', { receive: checked.receive });
    } catch (e) { debugLog('check_perms_err', { error: String(e) }); }

    // Request permission
    const permStatus = await PushNotifications.requestPermissions();

    console.log('[Push] Permission status:', permStatus.receive);
    debugLog('perm_status', { receive: permStatus.receive });
    if (permStatus.receive !== 'granted') {
      console.log('[Push] Permission not granted — skipping register');
      return false;
    }

    await attachPushListenersOnce();

    const ok = await registerWithRetry();
    pushInitialized = ok;
    if (ok) pushLastReverifyTs = Date.now();
    return ok;
  } catch (error) {
    console.error('[Push] Setup error:', error);
    return false;
  }
};

// Called from the appStateChange listener when the app returns to
// foreground. Re-attempts register() on a throttle — iOS drops APS
// subscriptions on its own (OS updates, low storage, background
// termination) and this is our self-heal path. No-op when push was
// never successfully initialized (nothing to re-verify) or when we
// re-verified recently.
export const pushForegroundReverify = async () => {
  if (!isNative || !pushInitialized) return false;
  const now = Date.now();
  if (now - pushLastReverifyTs < PUSH_REVERIFY_INTERVAL_MS) return false;
  pushLastReverifyTs = now;
  debugLog('foreground_reverify_start');
  const ok = await registerWithRetry();
  debugLog('foreground_reverify_done', { ok });
  return ok;
};

/**
 * Trigger haptic feedback
 */
export const hapticFeedback = async (style = 'medium') => {
  if (!isNative) return;

  try {
    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy
    }[style] || ImpactStyle.Medium;

    await Haptics.impact({ style: impactStyle });
  } catch (error) {
    console.error('[Haptics] Error:', error);
  }
};

/**
 * Get device info
 */
export const getDeviceInfo = () => {
  return {
    platform,
    isNative,
    isIOS: platform === 'ios',
    isAndroid: platform === 'android',
    isWeb: platform === 'web'
  };
};

export default {
  isNative,
  platform,
  initializeCapacitor,
  initializePushNotifications,
  hapticFeedback,
  getDeviceInfo
};
